import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";

import type {
  AdminDepartureConsent,
  AdminDepartureDestination,
  AdminDepartureResult,
} from "./adminDepartureCoordinator";
import { adminDepartureCoordinator } from "./adminDepartureRuntime";

import { EMAIL_ASSISTANT_CONTEXT_QUERY_KEYS } from "@/vnext/routes/messaging/email-assistant/EmailAssistantQueryContextModel";

const assistantKeys = [
  "emailAssistant",
  "eaPersonalDraftId",
  "eaDraftOrganisationId",
  "eaManualDraftId",
  "eaManualDraftOrganisationId",
  ...EMAIL_ASSISTANT_CONTEXT_QUERY_KEYS,
];

/** Assistant-only URL changes leave a source-page editor in place. */
export function affectsSourceEditor(
  current: { pathname: string; search: string },
  destination: AdminDepartureDestination,
): boolean {
  if (destination.kind === "exit" || !destination.to) return true;
  try {
    const source = new URL(
      `${current.pathname}${current.search}`,
      "https://admin.invalid",
    );
    const next = new URL(destination.to, source);
    if (source.origin !== next.origin || source.pathname !== next.pathname)
      return true;
    for (const key of assistantKeys) {
      source.searchParams.delete(key);
      next.searchParams.delete(key);
    }
    return source.searchParams.toString() !== next.searchParams.toString();
  } catch {
    return true;
  }
}

/** A prompt grants consent only; the draft remains owned until navigation. */
export function useAdminDepartureOwner({
  when,
  busy,
  revision,
  readRevision,
  isAffected = affectsSourceEditor,
}: {
  when: boolean;
  busy: boolean;
  revision: string;
  readRevision?: () => string;
  isAffected?: typeof affectsSourceEditor;
}) {
  const location = useLocation();
  const revisionEpoch = useRef({ value: revision, epoch: 0 });
  if (revisionEpoch.current.value !== revision) {
    revisionEpoch.current = {
      value: revision,
      epoch: revisionEpoch.current.epoch + 1,
    };
  }
  const current = useRef({
    when,
    busy,
    revision: revisionEpoch.current.epoch,
    readRevision,
    isAffected,
    location,
  });
  current.current = {
    when,
    busy,
    revision: revisionEpoch.current.epoch,
    readRevision,
    isAffected,
    location,
  };
  const pending = useRef<AdminDepartureConsent | null>(null);
  const [promptOpen, setPromptOpen] = useState(false);
  const [retry, setRetry] = useState<{
    resume: () => AdminDepartureResult;
    onlyIfDirty: boolean;
  } | null>(null);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    const unregister = adminDepartureCoordinator.register({
      isAffected: (destination) =>
        current.current.isAffected(current.current.location, destination),
      read: () => ({
        active: current.current.when || current.current.busy,
        busy: current.current.busy,
        revision: `${current.current.revision}:${current.current.readRevision?.() ?? ""}`,
      }),
      requestConsent: (consent) => {
        pending.current = consent;
        setPromptOpen(true);
        return () => {
          if (pending.current === consent) {
            pending.current = null;
            setPromptOpen(false);
          }
        };
      },
    });
    return () => {
      mounted.current = false;
      unregister();
    };
  }, []);

  useEffect(() => {
    if (!retry || busy) return;
    setRetry(null);
    if (!retry.onlyIfDirty || current.current.when) retry.resume();
  }, [busy, retry]);

  const resolve = (approve: boolean) => {
    const consent = pending.current;
    pending.current = null;
    setPromptOpen(false);
    if (approve) consent?.approve();
    else consent?.cancel();
  };
  return {
    promptOpen,
    cancel: () => resolve(false),
    approve: () => resolve(true),
    suspend: () => {
      const origin = current.current.location;
      const resume = pending.current?.suspend();
      if (!resume) return null;
      return () => {
        const latest = current.current.location;
        if (
          !mounted.current ||
          latest.key !== origin.key ||
          latest.pathname !== origin.pathname ||
          latest.search !== origin.search ||
          latest.hash !== origin.hash ||
          latest.state !== origin.state
        )
          return "blocked" as const;
        return resume();
      };
    },
    retryWhenReady: (
      resume: () => AdminDepartureResult,
      onlyIfDirty = false,
    ) => {
      if (mounted.current) setRetry({ resume, onlyIfDirty });
    },
  };
}
